import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { AlertController, NavController } from '@ionic/angular';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from '../app/auth/auth.service';
import { AuthStateService, Show } from '../app/auth/auth-state.service';

/**
 * Defense-in-depth guard for routes that require a boolean feature flag
 * on the active show (e.g. {@code hasPerformerFlyInspections}).
 * <p>
 * The menu already hides links for shows where the flag is off, but a
 * user could deep-link past that — this guard catches it.
 * <p>
 * The guard returns an Observable that waits for {@code AuthService.isAuthenticated}
 * to settle (non-null) before reading {@code activeShow}. This is required because
 * Angular evaluates {@code canActivate} guards in parallel: if this guard returned
 * a synchronous {@code false} while {@code AuthGuard}'s observable was still
 * pending {@code loadCurrentUser()}, Angular would cancel the navigation before
 * {@code activeShow} got populated. Particularly visible on {@code loadComponent}
 * routes, where {@code canLoad} doesn't pre-warm the auth state the way it does
 * for {@code loadChildren} module routes.
 * <p>
 * Usage:
 * <pre>
 * {
 *     path: 'magicaluna/performer-fly',
 *     canActivate: [AuthGuard, NoShowGuard, PrivilegeGuard, ShowFlagGuard],
 *     data: { privilege: 'magicaluna_inspections_view',
 *             showFlag: 'hasPerformerFlyInspections' }
 * }
 * </pre>
 */
@Injectable({
    providedIn: 'root'
})
export class ShowFlagGuard implements CanActivate {

    constructor(
        private navController: NavController,
        private authStateService: AuthStateService,
        private authService: AuthService,
        private alertController: AlertController
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        const flagName = route.data.showFlag as keyof Show | undefined;
        if (!flagName) {
            // No flag configured for this route — nothing to enforce.
            return of(true);
        }

        return this.authService.isAuthenticated.pipe(
            filter(val => val !== null),
            take(1),
            map(() => this.checkFlag(flagName))
        );
    }

    private checkFlag(flagName: keyof Show): boolean {
        const activeShow = this.authStateService.activeShow();
        if (activeShow && activeShow[flagName] === true) {
            return true;
        }

        // Flag is off (or no active show). Bounce back to the dashboard
        // and surface a friendly alert — same UX as PrivilegeGuard's
        // no-access path so the user isn't confused by a different
        // failure mode.
        void this.showFeatureDisabledAlert();
        this.navController.navigateRoot(['/dashboard']);
        return false;
    }

    private async showFeatureDisabledAlert(): Promise<void> {
        const alert = await this.alertController.create({
            header: 'Functie niet beschikbaar',
            message: 'Deze functie is niet ingeschakeld voor de actieve show.',
            buttons: ['OK']
        });
        await alert.present();
    }
}
